/**
 * Outreach message templates for the talent coordinator, taken from her spreadsheet's Message
 * generator (18 Sep 2026) with the sender's name and title filled from the signed-in staff member.
 * Pure functions; the Day plan page renders a preview she can edit before sending.
 */

export const TEMPLATES = [
  { key: 'photographer', label: 'Photographer outreach', roles: ['photographer', 'second_photographer'] },
  { key: 'videographer', label: 'Videographer outreach', roles: ['videographer'] },
  { key: 'follow_up', label: 'Follow-up (sent a message on the site)', roles: ['photographer', 'videographer', 'second_photographer'] }
]

export function defaultTemplateFor(role) {
  return role === 'videographer' ? 'videographer' : 'photographer'
}

/** "Nautica O." from "Nautica O'Neill"; falls back to the whole name or "the team". */
export function shortSignature(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return 'the team'
  if (parts.length === 1) return parts[0]
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`
}

export function firstName(fullName) {
  return String(fullName || '').trim().split(/\s+/)[0] || ''
}

/** "September 19, 2026" in the business time zone, from a YYYY-MM-DD date. The year is always written: talent are
 * asked about dates a year or more out, and a bare "September 19" has been read as this year (Nautica, 22 Sep 2026). */
export function longMonthDay(ymd) {
  if (!ymd) return ''
  const d = new Date(String(ymd).slice(0, 10) + 'T12:00:00Z')
  if (Number.isNaN(d.getTime())) return String(ymd)
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Denver' })
}

/** The role a message asks the person to fill, in words: "lead photographer", "second photographer", "videographer". */
export const ROLE_WORDS = { photographer: 'lead photographer', second_photographer: 'second photographer', videographer: 'videographer' }
export function roleWords(role) {
  return ROLE_WORDS[role] || null
}

/**
 * Render one template.
 * ctx: { recipientName, hours, city, eventDate, role, senderName, senderTitle, company }
 * `role` is the slot being offered (photographer / second_photographer / videographer); the message names it so the
 * person knows what they are being asked for (Nautica, 22 Sep 2026).
 */
export function renderTemplate(key, ctx = {}) {
  const company = ctx.company || 'Love & Photos'
  const sender = shortSignature(ctx.senderName)
  const senderFirst = firstName(ctx.senderName) || sender
  const title = ctx.senderTitle || 'Talent Coordinator'
  const greetName = firstName(ctx.recipientName)
  const hours = ctx.hours ? `${ctx.hours} hour ` : ''
  const where = ctx.city ? ` in ${ctx.city}` : ''
  const when = ctx.eventDate ? ` on ${longMonthDay(ctx.eventDate)}` : ''
  const asked = roleWords(ctx.role) ? ` This would be for the ${roleWords(ctx.role)} role.` : ''

  if (key === 'follow_up') {
    return `Hi${greetName ? ' ' + greetName : ''}! This is ${senderFirst} from ${company}.\n\n` +
      `I just sent you a message through our site regarding an upcoming booking. Let me know if you have any trouble seeing it!\n\n` +
      `Thanks,\n${sender} -- ${title}`
  }
  const isVideo = key === 'videographer'
  const body = isVideo
    ? `We have a ${hours}event${where}${when} that could use your videography skills.${asked} Let me know if you're available!`
    : `We have a ${hours}shoot${where}${when} that could use your photography skills.${asked} Your portfolio is perfect for what we're looking for!! Let me know if you're available!`
  return `Hi there${greetName ? ' ' + greetName : ''}! This is ${senderFirst} from ${company}.\n\n` +
    `I hope you're doing well!! ${body}\n\n` +
    `Looking forward to hearing from you,\n${signatureBlock(ctx.senderName, ctx.senderTitle, company)}`
}

/**
 * The sign-off lines. An admin's title resolves to the company name, which used to print "Love & Photos" twice
 * (Trera's screenshot, 18 Sep 2026); when the title and the company are the same, one line is enough.
 */
export function signatureBlock(senderName, senderTitle, company) {
  const sender = shortSignature(senderName)
  const title = senderTitle || 'Talent Coordinator'
  const co = company || 'Love & Photos'
  return title.trim().toLowerCase() === co.trim().toLowerCase() ? `${sender}\n${co}` : `${sender}\n${title}\n${co}`
}

/** Replace the recipient's first name in an already-edited message body, so one edit serves many recipients. */
export const RECIPIENT_TOKEN = '{first_name}'
export function personalize(text, recipientName) {
  return String(text || '').split(RECIPIENT_TOKEN).join(firstName(recipientName))
}
